import React from 'react';
import { defineMessages } from 'react-intl-next';
import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import type { FlagsStateContainer, FlagDescriptor } from '@confluence/flags';

import { CopyLinkFlagComponent } from '../components/CopyLinkFlagComponent';

// We want the flag to stay for about 8000ms + view page load time 3000ms (according to FMP)
export const PUBLISH_SUCCESS_FLAG_AUTO_CLOSE_TIME = 11000;

type ShowPublishSuccessFlagsProps = {
	isFirstTimePublish: boolean;
	shouldTimeout?: boolean;
	spaceName?: string;
	flags: FlagsStateContainer;
	title: string | null;
	intl: IntlShape;
	siteTitle?: string;
	isCompanyHub?: boolean;
	copyContentLink?: () => void;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	isConvertingFromLivePage?: boolean;
};

const i18n = defineMessages({
	publishSuccessTitle: {
		id: 'editor-features.success.publish.title',
		description: 'Success title message when a page is published',
		defaultMessage: 'Published!',
	},
	updateSuccessTitle: {
		id: 'editor-features.success.update.title',
		description: 'Success title message when a page is updated',
		defaultMessage: 'Updated!',
	},
	convertAndPublishSuccessTitle: {
		id: 'editor-features.success.convertAndPublish.title',
		description: 'Success title message when a live doc is converted to a page via publishing',
		defaultMessage: 'Converted and published!',
	},
	publishSuccessMessageWithSpaceName: {
		id: 'editor-features.user.publish.success.with.space.name',
		description: 'Success message containing space name when publishing a page',
		defaultMessage: '{pageTitle} was successfully published in the {spaceName} space',
	},
	updateSuccessMessageWithSpaceName: {
		id: 'editor-features.user.update.success.with.space.name',
		description: 'Success message containing space name when updating a page',
		defaultMessage: '{pageTitle} was successfully updated in the {spaceName} space',
	},
	publishSuccessMessage: {
		id: 'editor-features.user.publish.success',
		description: 'Success message when publishing a page',
		defaultMessage: '{pageTitle} was successfully published',
	},
	updateSuccessMessage: {
		id: 'editor-features.user.update.success',
		description: 'Success message when updating a page',
		defaultMessage: '{pageTitle} was successfully updated',
	},
	convertAndPublishSuccessMessage: {
		id: 'editor-features.user.convertAndPublish.success',
		description: 'Success message when updating a page',
		defaultMessage: '{pageTitle} was converted to a page and published.',
	},
	publishSuccessTitleWithSocial: {
		id: 'editor-features.publish.success.title.with.social',
		description: 'Success title message when a page is published with collaborator avatar',
		defaultMessage: 'Page successfully published',
	},
	publishSuccessMessageWithSocial: {
		id: 'editor-features.publish.success.message.with.social',
		description: 'Success message when a page is published with collaborator avatar',
		defaultMessage: 'Share this page to ensure your team can see and build on your contributions.',
	},
	sharePageCTA: {
		id: 'editor-features.publish.success.share-page',
		description: 'Call to action for sharing the page after publishing',
		defaultMessage: 'Share page',
	},
});

const getFlagStrings = ({
	isFirstTimePublish,
	hasSpaceName,
	isConvertingFromLivePage,
}: {
	isFirstTimePublish: boolean;
	hasSpaceName: boolean;
	isConvertingFromLivePage: boolean;
}): {
	title: MessageDescriptor;
	message: MessageDescriptor;
} => {
	if (isConvertingFromLivePage) {
		return {
			title: i18n.convertAndPublishSuccessTitle,
			message: i18n.convertAndPublishSuccessMessage,
		};
	} else {
		if (isFirstTimePublish) {
			return {
				title: i18n.publishSuccessTitle,
				message: hasSpaceName
					? i18n.publishSuccessMessageWithSpaceName
					: i18n.publishSuccessMessage,
			};
		} else {
			return {
				title: i18n.updateSuccessTitle,
				message: hasSpaceName ? i18n.updateSuccessMessageWithSpaceName : i18n.updateSuccessMessage,
			};
		}
	}
};

export const showPublishSuccessFlags = async ({
	isFirstTimePublish,
	shouldTimeout = true,
	spaceName,
	flags,
	title,
	intl,
	copyContentLink,
	createAnalyticsEvent,
	isConvertingFromLivePage = false,
}: ShowPublishSuccessFlagsProps) => {
	const flagStrings = getFlagStrings({
		isFirstTimePublish,
		hasSpaceName: !!spaceName,
		isConvertingFromLivePage,
	});
	const messageData = { spaceName, pageTitle: <b>{title}</b> };

	let flagProps: Partial<FlagDescriptor> = {};

	if (copyContentLink && createAnalyticsEvent) {
		flagProps = {
			description: (
				<CopyLinkFlagComponent
					copyContentLink={copyContentLink}
					successPublishMessage={intl.formatMessage(flagStrings.message, messageData)}
					createAnalyticsEvent={createAnalyticsEvent}
					isFirstTimePublish={isFirstTimePublish}
				/>
			),
		};
	}

	const publishSuccessFlag = await flags.showFlag({
		type: 'success-circle',
		title: intl.formatMessage(flagStrings.title),
		description: intl.formatMessage(flagStrings.message, messageData),
		close: 'never',
		messageId: 'confluence-frontend-editor-features-publish-success-flag',
		messageType: 'transactional',
		...flagProps,
	});
	if (shouldTimeout) {
		setTimeout(() => flags.hideFlag(publishSuccessFlag.id), PUBLISH_SUCCESS_FLAG_AUTO_CLOSE_TIME);
	}
};
